import React from 'react';
import { Dialog, DialogProps, Slide, useMediaQuery, useTheme } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';

// Slide-up transition used for the mobile bottom-sheet presentation.
const SlideUpTransition = React.forwardRef(function SlideUpTransition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * Drop-in replacement for MUI's <Dialog> that presents as a bottom sheet on
 * mobile: it slides up from the bottom, spans the full width, and rounds only
 * its top corners. On larger screens it behaves like a normal centered Dialog.
 *
 * Any `sx` / `PaperProps.sx` passed by the caller is preserved; the mobile
 * sheet styles are applied last so they win on small screens.
 */
const ResponsiveDialog: React.FC<DialogProps> = ({
  children,
  PaperProps,
  TransitionComponent,
  sx,
  ...rest
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const callerPaperSx = PaperProps?.sx;
  const callerPaperSxArray = Array.isArray(callerPaperSx)
    ? callerPaperSx
    : callerPaperSx
    ? [callerPaperSx]
    : [];

  const mobilePaperSx = {
    m: 0,
    width: '100%',
    maxWidth: '100%',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    maxHeight: '92vh',
    pb: 'env(safe-area-inset-bottom)',
  };

  return (
    <Dialog
      {...rest}
      TransitionComponent={isMobile ? SlideUpTransition : TransitionComponent}
      sx={[
        ...(isMobile ? [{ '& .MuiDialog-container': { alignItems: 'flex-end' } }] : []),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      PaperProps={{
        ...PaperProps,
        sx: [...callerPaperSxArray, ...(isMobile ? [mobilePaperSx] : [])],
      }}
    >
      {children}
    </Dialog>
  );
};

export default ResponsiveDialog;
